import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
    FormBuilder,
    FormGroup,
    Validators,
    ReactiveFormsModule,
} from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatRadioModule } from '@angular/material/radio';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { AyudasService, Ayuda } from '../../../services/ayudas.service';
import { catchError, finalize } from 'rxjs/operators';
import { of } from 'rxjs';

@Component({
    selector: 'app-edit-ayuda-page',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatCardModule,
        MatFormFieldModule,
        MatInputModule,
        MatButtonModule,
        MatRadioModule,
        MatCheckboxModule,
        MatSnackBarModule,
        MatProgressSpinnerModule,
    ],
    templateUrl: './edit-ayuda-page.component.html',
    styleUrl: './edit-ayuda-page.component.scss',
})
export class EditAyudaPageComponent implements OnInit {
    form: FormGroup;
    isLoading = false;
    loadError = false;
    ayuda: Ayuda | null = null;
    selectedFile: File | null = null;

    constructor(
        private fb: FormBuilder,
        private ayudasService: AyudasService,
        private snackBar: MatSnackBar,
        private router: Router,
        private route: ActivatedRoute
    ) {
        this.form = this.fb.group({
            titulo: ['', [Validators.required, Validators.maxLength(200)]],
            tipo: ['documento', Validators.required],
            url: [''],
            descripcion: ['', Validators.maxLength(2000)],
            activo: [true],
            orden: [0, [Validators.required, Validators.min(0)]],
        });
        this.form.get('tipo')?.valueChanges.subscribe(() => this.updateValidators());
    }

    private updateValidators(): void {
        const url = this.form.get('url');
        if (this.form.get('tipo')?.value === 'video') {
            url?.setValidators([Validators.required, Validators.maxLength(1000)]);
        } else {
            url?.clearValidators();
        }
        url?.updateValueAndValidity();
    }

    ngOnInit(): void {
        const id = this.route.snapshot.paramMap.get('id');
        if (!id) {
            this.router.navigate(['/mantenedor-ayudas']);
            return;
        }
        this.isLoading = true;
        this.ayudasService
            .getById(id)
            .pipe(
                catchError(() => {
                    this.loadError = true;
                    return of(null);
                }),
                finalize(() => (this.isLoading = false))
            )
            .subscribe((res) => {
                if (res?.success && res.data) {
                    this.ayuda = res.data;
                    this.form.patchValue({
                        titulo: res.data.titulo,
                        tipo: res.data.tipo,
                        url: res.data.tipo === 'video' ? res.data.urlOrPath : '',
                        descripcion: res.data.descripcion ?? '',
                        activo: res.data.activo,
                        orden: res.data.orden,
                    });
                }
            });
    }

    onFileChange(event: Event): void {
        const input = event.target as HTMLInputElement;
        this.selectedFile = input.files?.[0] ?? null;
    }

    onSubmit(): void {
        if (this.form.invalid || !this.ayuda) return;
        this.form.markAllAsTouched();
        const tipo = this.form.get('tipo')?.value;
        if (tipo === 'video' && !this.form.get('url')?.value?.trim()) {
            this.snackBar.open('Ingrese la URL de YouTube', 'Cerrar', {
                duration: 4000,
                panelClass: ['error-snackbar'],
            });
            return;
        }

        const formData = new FormData();
        formData.append('titulo', this.form.get('titulo')?.value?.trim() ?? '');
        formData.append('tipo', tipo);
        formData.append('activo', String(this.form.get('activo')?.value ?? true));
        formData.append('orden', String(this.form.get('orden')?.value ?? 0));
        const desc = this.form.get('descripcion')?.value?.trim();
        if (desc) formData.append('descripcion', desc);
        if (tipo === 'video') {
            formData.append('url', this.form.get('url')?.value?.trim() ?? '');
        }
        if (this.selectedFile) formData.append('file', this.selectedFile);

        this.isLoading = true;
        this.ayudasService
            .update(this.ayuda.id, formData)
            .pipe(
                catchError((err) => {
                    const msg = err.error?.message || 'Error al actualizar';
                    this.snackBar.open(msg, 'Cerrar', {
                        duration: 5000,
                        panelClass: ['error-snackbar'],
                    });
                    return of(null);
                }),
                finalize(() => (this.isLoading = false))
            )
            .subscribe((res) => {
                if (res?.success) {
                    this.snackBar.open('Ayuda actualizada correctamente', 'Cerrar', {
                        duration: 3000,
                        panelClass: ['success-snackbar'],
                    });
                    this.router.navigate(['/mantenedor-ayudas']);
                }
            });
    }

    onCancel(): void {
        this.router.navigate(['/mantenedor-ayudas']);
    }
}
